import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLearning } from '../../context/LearningContext';
import CardRenderer from './CardRenderer';
import RatingButtons from './RatingButtons';
import './ReviewSession.css';

// ─── Lesson phase card — shows front + back together ────────────────────────

function LessonCard({ card }) {
  const bloomColors = {
    remember: '#00ff88', understand: '#00f5ff', apply: '#ff6b35',
    analyze: '#f59e0b', evaluate: '#ff3366', create: '#8b5cf6',
  };
  const color = bloomColors[card.bloom_level] || 'var(--accent)';

  return (
    <div className="lesson-card">
      <div className="lesson-card-meta">
        <span className="lesson-bloom-dot" style={{ background: color }} />
        <span className="lesson-bloom-label" style={{ color }}>{card.bloom_level}</span>
        <span className="lesson-type-label">{card.card_type.replace('_', ' ')}</span>
      </div>

      <div className="lesson-question">
        <div className="lesson-question-label">Question</div>
        <p>{card.front}</p>
      </div>

      <div className="lesson-divider">
        <span>Answer</span>
      </div>

      <div className="lesson-answer">
        {card.back.split('\n').map((line, i) =>
          line.trim() ? <p key={i}>{line}</p> : <br key={i} />
        )}
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function ReviewSession() {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { submitReview } = useLearning();

  const [cards, setCards] = useState([]);
  const [isLesson, setIsLesson] = useState(false);
  // phase: 'learn' | 'transition' | 'test' | 'complete'
  const [phase, setPhase] = useState('test');
  const [learnIndex, setLearnIndex] = useState(0);
  const [testIndex, setTestIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(Date.now());

  useEffect(() => {
    async function loadSession() {
      try {
        const res = await fetch(`/api/learning/sessions/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          setCards(data.session.cards);
          const lesson = data.session.lesson ?? false;
          setIsLesson(lesson);
          setPhase(lesson ? 'learn' : 'test');
          if (!lesson) setTestIndex(data.session.progress);
        }
      } catch (err) {
        console.error('Failed to load session:', err);
      } finally {
        setLoading(false);
      }
    }
    loadSession();
  }, [sessionId]);

  // ── Learn phase handlers ───────────────────────────────────────────────────

  const handleLearnNext = () => {
    if (learnIndex + 1 >= cards.length) {
      setPhase('transition');
    } else {
      setLearnIndex(i => i + 1);
    }
  };

  const handleLearnPrev = () => {
    if (learnIndex > 0) setLearnIndex(i => i - 1);
  };

  const handleStartTest = () => {
    setPhase('test');
    setTestIndex(0);
    setRevealed(false);
    timerRef.current = Date.now();
  };

  // ── Test phase handlers ────────────────────────────────────────────────────

  const handleReveal = () => setRevealed(true);

  const handleRate = async (rating) => {
    const card = cards[testIndex];
    const durationMs = Date.now() - timerRef.current;
    try {
      await submitReview(sessionId, card.id, rating, durationMs);
      setResults(prev => [...prev, { rating }]);
      if (testIndex + 1 >= cards.length) {
        setPhase('complete');
      } else {
        setTestIndex(i => i + 1);
        setRevealed(false);
        timerRef.current = Date.now();
      }
    } catch (err) {
      console.error('Failed to submit review:', err);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <div className="review-loading">Loading…</div>;

  if (cards.length === 0) {
    return (
      <div className="review-complete">
        <h2>Nothing to study</h2>
        <p style={{ color: 'var(--text-secondary)' }}>No cards found for this session.</p>
        <button className="btn-primary" onClick={() => navigate('/learn')}>Back to Dashboard</button>
      </div>
    );
  }

  // ── Phase: learn ───────────────────────────────────────────────────────────

  if (phase === 'learn') {
    const card = cards[learnIndex];
    const isLast = learnIndex === cards.length - 1;
    return (
      <div className="review-session">
        <div className="review-header">
          <div className="lesson-phase-label">
            <span className="phase-chip learn-chip">Study</span>
            <span className="review-progress">Topic {learnIndex + 1} of {cards.length}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${((learnIndex + 1) / cards.length) * 100}%` }} />
          </div>
        </div>

        <div className="review-card-area">
          <LessonCard card={card} />
        </div>

        <div className="lesson-nav">
          <button
            className="btn-lesson-prev"
            onClick={handleLearnPrev}
            disabled={learnIndex === 0}
          >
            ← Prev
          </button>
          <button
            className={isLast ? 'btn-lesson-finish' : 'btn-lesson-next'}
            onClick={handleLearnNext}
          >
            {isLast ? 'Finish studying →' : 'Next →'}
          </button>
        </div>
      </div>
    );
  }

  // ── Phase: transition ──────────────────────────────────────────────────────

  if (phase === 'transition') {
    return (
      <div className="review-complete">
        <div className="transition-icon">🎓</div>
        <h2>Ready to test yourself?</h2>
        <p style={{ color: 'var(--text-secondary)', maxWidth: 360, textAlign: 'center' }}>
          You've reviewed all {cards.length} topics. Now let's see how much you retained.
        </p>
        <button className="btn-primary" onClick={handleStartTest}>
          Start test →
        </button>
        <button
          className="btn-ghost"
          onClick={() => { setLearnIndex(0); setPhase('learn'); }}
          style={{ marginTop: '-0.5rem' }}
        >
          Review topics again
        </button>
      </div>
    );
  }

  // ── Phase: complete ────────────────────────────────────────────────────────

  if (phase === 'complete') {
    const accuracy = results.length > 0
      ? Math.round((results.filter(r => r.rating >= 3).length / results.length) * 100)
      : 0;
    return (
      <div className="review-complete">
        <div className="transition-icon">{accuracy >= 80 ? '🏆' : accuracy >= 60 ? '📈' : '💪'}</div>
        <h2>{t('learn.session.complete')}</h2>
        <div className="complete-stats">
          <div className="stat">{t('learn.session.reviewed')}: {results.length}</div>
          <div className="stat">{t('learn.session.accuracy')}: {accuracy}%</div>
        </div>
        {isLesson && accuracy < 80 && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', textAlign: 'center', maxWidth: 320 }}>
            Score below 80%? Click the path again to review the topics and retry.
          </p>
        )}
        <button className="btn-primary" onClick={() => navigate('/learn')}>
          {t('learn.session.backToDashboard')}
        </button>
      </div>
    );
  }

  // ── Phase: test ────────────────────────────────────────────────────────────

  const card = cards[testIndex];

  return (
    <div className="review-session">
      <div className="review-header">
        {isLesson && (
          <div className="lesson-phase-label">
            <span className="phase-chip test-chip">Test</span>
            <span className="review-progress">
              {t('learn.session.progress', { current: testIndex + 1, total: cards.length })}
            </span>
          </div>
        )}
        {!isLesson && (
          <div className="review-progress">
            {t('learn.session.progress', { current: testIndex + 1, total: cards.length })}
          </div>
        )}
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${((testIndex + 1) / cards.length) * 100}%` }} />
        </div>
      </div>

      <div className="review-card-area">
        <CardRenderer card={card} revealed={revealed} />
      </div>

      <div className="review-actions">
        {!revealed ? (
          <button className="btn-reveal" onClick={handleReveal}>
            {t('learn.session.reveal')}
          </button>
        ) : (
          <RatingButtons onRate={handleRate} />
        )}
      </div>

      <div className="review-footer">
        <span className={`bloom-badge bloom-${card.bloom_level}`}>
          {t(`learn.bloomLevels.${card.bloom_level}`)}
        </span>
        <span className="card-type-badge">
          {t(`learn.cardTypes.${card.card_type}`)}
        </span>
        {isLesson && (
          <button
            className="btn-ghost btn-small"
            onClick={() => { setLearnIndex(testIndex); setPhase('learn'); }}
            title="Go back to study this topic"
          >
            📖 Review
          </button>
        )}
      </div>
    </div>
  );
}
